import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import Thread from '@/components/tickets/Thread';
import TicketDialog from '@/components/account/TicketDialog';
import { PRIORITY_STYLES, TICKET_STATUS_STYLES } from '@/shared/constants';
import { TICKET_FIELDS } from '@/shared/fields';
import { committed, idOf, nowIso } from '@/shared/data';
import { formatLocalDate, formatRelativeTime, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/account/tickets/:id', layout: 'owner', access: "profile === 'Customer_c'" };

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: ticket, loading, error, run } = useFetch(
    () => sdk.table('tickets_c').select(TICKET_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/account/tickets" label="Back to Support" />;
  const gate = recordGate({ back, loading, record: ticket, error, run, what: 'Ticket' });
  if (gate) return gate;

  const closed = ticket.status_c === 'Closed';
  const orderId = idOf(ticket.order_c);

  async function setStatus(status, message) {
    const res = await sdk.table('tickets_c').update({ Id: ticket.Id, status_c: status });
    if (!committed(res, message)) return false;
    run();
    return true;
  }

  async function replied() {
    await sdk.table('tickets_c').update({ Id: ticket.Id, status_c: 'Open', last_reply_at_c: nowIso() });
    run();
  }

  async function remove() {
    const res = await sdk.table('tickets_c').remove(ticket.Id);
    if (!committed(res, 'Ticket deleted')) return false;
    navigate('/account/tickets');
    return true;
  }

  return (
    <div className="mx-auto max-w-5xl">
      {back}
      <PageHeader
        eyebrow={`${ticket.category_c || 'Support'} · opened ${formatLocalDate(ticket.CreatedOn, DATE_FORMATS.SHORT)}`}
        title={ticket.Name}
        actions={
          <>
            <StatusBadge value={ticket.status_c} styles={TICKET_STATUS_STYLES} />
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            {closed ? (
              <ConfirmAction
                label="Reopen"
                icon="RotateCcw"
                title="Reopen this ticket?"
                description="It goes back in the support queue and you can reply again."
                busyLabel="Reopening…"
                onConfirm={() => setStatus('Open', 'Ticket reopened')}
              />
            ) : (
              <ConfirmAction
                label="Close ticket"
                icon="CircleCheck"
                title="Close this ticket?"
                description="Close it when your problem is solved. You can reopen it later."
                busyLabel="Closing…"
                onConfirm={() => setStatus('Closed', 'Ticket closed')}
              />
            )}
            <ConfirmDelete
              title="Delete this ticket?"
              description="The ticket leaves your account. This cannot be undone."
              onConfirm={remove}
            />
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Thread ticketId={ticket.Id} author="Customer" closed={closed} onReplied={replied} />

        <Panel title="Details" icon="Info">
          <dl className="divide-y divide-border">
            <DetailRow label="Status">
              <StatusBadge value={ticket.status_c} styles={TICKET_STATUS_STYLES} />
            </DetailRow>
            <DetailRow label="Priority">
              <StatusBadge value={ticket.priority_c} styles={PRIORITY_STYLES} />
            </DetailRow>
            <DetailRow label="Category">{ticket.category_c}</DetailRow>
            <DetailRow label="Order">
              {orderId ? (
                <Link to={`/account/orders/${orderId}`} className="font-mono text-primary hover:underline">
                  {ticket.order_c?.Name ?? 'View order'}
                </Link>
              ) : null}
            </DetailRow>
            <DetailRow label="Email">{ticket.email_c}</DetailRow>
            <DetailRow label="Opened">{formatLocalDate(ticket.CreatedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
            <DetailRow label="Last reply">
              {ticket.last_reply_at_c ? formatRelativeTime(ticket.last_reply_at_c) : null}
            </DetailRow>
          </dl>
        </Panel>
      </div>

      <TicketDialog open={editOpen} onOpenChange={setEditOpen} ticket={ticket} onSaved={run} />
    </div>
  );
}
